import * as React from 'react'

import { mergeProps } from '@base-ui-components/react/merge-props'
import { useRender } from '@base-ui-components/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDown, LucideIcon } from 'lucide-react'

import { cn } from '@/utils'

const buttonVariants = cva(
  'cursor-pointer group whitespace-nowrap focus-visible:outline-hidden inline-flex items-center justify-center has-data-[arrow=true]:justify-between whitespace-nowrap text-sm font-medium ring-offset-background transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-60 [&_svg]:shrink-0',
  {
    compoundVariants: [
      // Icons opacity for default mode
      {
        className:
          '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
        mode: 'default',
        variant: 'ghost'
      },
      {
        className:
          '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
        mode: 'default',
        variant: 'outline'
      },
      {
        className:
          '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
        mode: 'default',
        variant: 'dashed'
      },
      {
        className:
          '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
        mode: 'default',
        variant: 'secondary'
      },

      // Icons opacity for default mode
      {
        className:
          '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
        mode: 'input',
        variant: 'outline'
      },
      {
        className:
          '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
        mode: 'icon',
        variant: 'outline'
      },

      // Auto height
      {
        autoHeight: true,
        className: 'h-auto min-h-7',
        size: 'xs'
      },
      {
        autoHeight: true,
        className: 'h-auto min-h-9',
        size: 'md'
      },
      {
        autoHeight: true,
        className: 'h-auto min-h-8',
        size: 'sm'
      },
      {
        autoHeight: true,
        className: 'h-auto min-h-10',
        size: 'lg'
      },

      // Shadow support
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'default',
        variant: 'primary'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'default',
        variant: 'mono'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'default',
        variant: 'secondary'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'default',
        variant: 'outline'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'default',
        variant: 'dashed'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'default',
        variant: 'destructive'
      },

      // Shadow support
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'icon',
        variant: 'primary'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'icon',
        variant: 'mono'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'icon',
        variant: 'secondary'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'icon',
        variant: 'outline'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'icon',
        variant: 'dashed'
      },
      {
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
        mode: 'icon',
        variant: 'destructive'
      },

      // Link
      {
        className:
          'font-medium text-primary hover:text-primary/90 [&_svg:not([role=img]):not([class*=text-])]:opacity-60 hover:underline hover:underline-offset-4 hover:decoration-solid',
        mode: 'link',
        underline: 'solid',
        variant: 'primary'
      },
      {
        className:
          'font-medium text-primary hover:text-primary/90 [&_svg]:opacity-60 hover:underline hover:underline-offset-4 hover:decoration-dashed decoration-1',
        mode: 'link',
        underline: 'dashed',
        variant: 'primary'
      },
      {
        className:
          'font-medium text-primary hover:text-primary/90 [&_svg:not([role=img]):not([class*=text-])]:opacity-60 underline underline-offset-4 decoration-solid',
        mode: 'link',
        underlined: 'solid',
        variant: 'primary'
      },
      {
        className:
          'font-medium text-primary hover:text-primary/90 [&_svg]:opacity-60 underline underline-offset-4 decoration-dashed decoration-1',
        mode: 'link',
        underlined: 'dashed',
        variant: 'primary'
      },

      {
        className:
          'font-medium text-inherit [&_svg:not([role=img]):not([class*=text-])]:opacity-60 hover:underline hover:underline-offset-4 hover:decoration-solid',
        mode: 'link',
        underline: 'solid',
        variant: 'inverse'
      },
      {
        className:
          'font-medium text-inherit [&_svg:not([role=img]):not([class*=text-])]:opacity-60 hover:underline hover:underline-offset-4 hover:decoration-dashed decoration-1',
        mode: 'link',
        underline: 'dashed',
        variant: 'inverse'
      },
      {
        className:
          'font-medium text-inherit [&_svg:not([role=img]):not([class*=text-])]:opacity-60 underline underline-offset-4 decoration-solid',
        mode: 'link',
        underlined: 'solid',
        variant: 'inverse'
      },
      {
        className:
          'font-medium text-inherit [&_svg:not([role=img]):not([class*=text-])]:opacity-60 underline underline-offset-4 decoration-dashed decoration-1',
        mode: 'link',
        underlined: 'dashed',
        variant: 'inverse'
      },

      {
        className:
          'font-medium text-foreground [&_svg:not([role=img]):not([class*=text-])]:opacity-60 hover:underline hover:underline-offset-4 hover:decoration-solid',
        mode: 'link',
        underline: 'solid',
        variant: 'foreground'
      },
      {
        className:
          'font-medium text-foreground [&_svg:not([role=img]):not([class*=text-])]:opacity-60 hover:underline hover:underline-offset-4 hover:decoration-dashed decoration-1',
        mode: 'link',
        underline: 'dashed',
        variant: 'foreground'
      },
      {
        className:
          'font-medium text-foreground [&_svg:not([role=img]):not([class*=text-])]:opacity-60 underline underline-offset-4 decoration-solid',
        mode: 'link',
        underlined: 'solid',
        variant: 'foreground'
      },
      {
        className:
          'font-medium text-foreground [&_svg:not([role=img]):not([class*=text-])]:opacity-60 underline underline-offset-4 decoration-dashed decoration-1',
        mode: 'link',
        underlined: 'dashed',
        variant: 'foreground'
      },

      // Ghost
      {
        appearance: 'ghost',
        className:
          'bg-transparent text-primary/90 hover:bg-primary/5 data-[state=open]:bg-primary/5',
        variant: 'primary'
      },
      {
        appearance: 'ghost',
        className:
          'bg-transparent text-destructive/90 hover:bg-destructive/5 data-[state=open]:bg-destructive/5',
        variant: 'destructive'
      },
      {
        className: 'text-muted-foreground',
        mode: 'icon',
        variant: 'ghost'
      },

      // Size
      {
        className: 'w-7 h-7 p-0 [[&_svg:not([class*=size-])]:size-3.5',
        mode: 'icon',
        size: 'sm'
      },
      {
        className: 'w-8.5 h-8.5 p-0 [&_svg:not([class*=size-])]:size-4',
        mode: 'icon',
        size: 'md'
      },
      {
        className: 'w-8.5 h-8.5 p-0 [&_svg:not([class*=size-])]:size-4',
        size: 'icon'
      },
      {
        className: 'w-10 h-10 p-0 [&_svg:not([class*=size-])]:size-4',
        mode: 'icon',
        size: 'lg'
      },

      // Input mode
      {
        className: 'font-normal text-muted-foreground',
        mode: 'input',
        placeholder: true,
        variant: 'outline'
      },
      {
        className: 'gap-1.25',
        mode: 'input',
        size: 'sm',
        variant: 'outline'
      },
      {
        className: 'gap-1.5',
        mode: 'input',
        size: 'md',
        variant: 'outline'
      },
      {
        className: 'gap-1.5',
        mode: 'input',
        size: 'lg',
        variant: 'outline'
      }
    ],
    defaultVariants: {
      appearance: 'default',
      mode: 'default',
      radius: 'md',
      size: 'md',
      variant: 'primary'
    },
    variants: {
      appearance: {
        default: '',
        ghost: ''
      },
      autoHeight: {
        false: '',
        true: ''
      },
      mode: {
        default:
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        icon: 'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        input: `
            justify-start font-normal hover:bg-background [&_svg]:transition-colors [&_svg]:hover:text-foreground data-[state=open]:bg-background 
            focus-visible:border-ring focus-visible:outline-hidden focus-visible:ring-[3px] focus-visible:ring-ring/30 
            [[data-state=open]>&]:border-ring [[data-state=open]>&]:outline-hidden [[data-state=open]>&]:ring-[3px] 
            [[data-state=open]>&]:ring-ring/30 
            aria-invalid:border-destructive/60 aria-invalid:ring-destructive/10 dark:aria-invalid:border-destructive dark:aria-invalid:ring-destructive/20
            in-data-[invalid=true]:border-destructive/60 in-data-[invalid=true]:ring-destructive/10  dark:in-data-[invalid=true]:border-destructive dark:in-data-[invalid=true]:ring-destructive/20
          `,
        link: 'text-primary h-auto p-0 bg-transparent rounded-none hover:bg-transparent data-[state=open]:bg-transparent'
      },
      placeholder: {
        false: '',
        true: 'text-muted-foreground'
      },
      radius: {
        full: 'rounded-full',
        md: 'rounded-md'
      },
      size: {
        icon: 'size-9 [&_svg:not([class*=size-])]:size-4 shrink-0',
        lg: 'h-10 px-4 text-sm gap-1.5 [&_svg:not([class*=size-])]:size-4',
        md: 'h-9 px-3 gap-1.5 text-sm [&_svg:not([class*=size-])]:size-4',
        sm: 'h-8 px-2.5 gap-1.25 text-xs [&_svg:not([class*=size-])]:size-3.5',
        xs: 'h-7 px-2 gap-1 text-xs [&_svg:not([class*=size-])]:size-3.5'
      },
      underline: {
        dashed: '',
        solid: ''
      },
      underlined: {
        dashed: '',
        solid: ''
      },
      variant: {
        dashed:
          'text-accent-foreground border border-input border-dashed bg-background hover:bg-accent hover:text-accent-foreground data-[state=open]:text-accent-foreground',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 data-[state=open]:bg-destructive/90',
        dim: 'text-muted-foreground hover:text-foreground data-[state=open]:text-foreground',
        foreground: '',
        ghost:
          'text-accent-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
        inverse: '',
        mono: 'bg-zinc-950 text-white dark:bg-zinc-300 dark:text-black hover:bg-zinc-950/90 dark:hover:bg-zinc-300/90 data-[state=open]:bg-zinc-950/90 dark:data-[state=open]:bg-zinc-300/90',
        outline:
          'bg-background text-accent-foreground border border-input hover:bg-accent data-[state=open]:bg-accent',
        primary:
          'bg-primary text-primary-foreground hover:bg-primary/90 data-[state=open]:bg-primary/90',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/90 data-[state=open]:bg-secondary/90'
      }
    }
  }
)

export interface ButtonProps
  extends useRender.ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  selected?: boolean
}

interface ButtonArrowProps extends React.SVGProps<SVGSVGElement> {
  icon?: LucideIcon
}

function Button({
  appearance,
  asChild = false,
  autoHeight,
  children,
  className,
  mode,
  placeholder = false,
  radius,
  render,
  selected,
  size,
  underline,
  underlined,
  variant,
  ...props
}: ButtonProps) {
  const defaultProps = {
    className: cn(
      buttonVariants({
        appearance,
        autoHeight,
        className,
        mode,
        placeholder,
        radius,
        size,
        underline,
        underlined,
        variant
      }),
      asChild && props.disabled && 'pointer-events-none opacity-50'
    ),
    'data-slot': 'button',
    ...(selected && { 'data-state': 'open' as const })
  }

  // Determine render element based on asChild prop
  const renderElement =
    asChild && React.isValidElement(children)
      ? (children as React.ReactElement<
          Record<string, unknown>,
          React.JSXElementConstructor<unknown> | string
        >)
      : render || <button />

  // When using asChild, children becomes the element props, otherwise use children normally
  const finalProps =
    asChild && React.isValidElement(children)
      ? mergeProps(defaultProps, props)
      : mergeProps(defaultProps, { ...props, children })

  const element = useRender({
    props: finalProps,
    render: renderElement
  })

  return element
}

function ButtonArrow({
  className,
  icon: Icon = ChevronDown,
  ...props
}: ButtonArrowProps) {
  return (
    <Icon
      className={cn('ms-auto -me-1', className)}
      data-slot="button-arrow"
      {...props}
    />
  )
}

export { Button, ButtonArrow, buttonVariants }
